// Entity-sport lookups for the deep-link gate (#416); see EntitySportGate.tsx.

import { api } from '@/services/api'
import type { SitePageKey } from './pages'

export type Params = Record<string, string | undefined>

/** Page key → the unscoped entity lookup for its route params. */
export const ENTITY_LOOKUPS: Partial<Record<SitePageKey, (p: Params) => string | null>> = {
  game: (p) => (p.id ? `/public/games/${encodeURIComponent(p.id)}` : null),
  player: (p) => (p.id ? `/public/players/${encodeURIComponent(p.id)}` : null),
  team: (p) => (p.id ? `/public/teams/${encodeURIComponent(p.id)}` : null),
  page: (p) => (p.slug ? `/public/pages/${encodeURIComponent(p.slug)}` : null),
  sponsor: (p) => (p.id ? `/public/sponsors/${encodeURIComponent(p.id)}` : null),
  venue: (p) => (p.id ? `/public/venues/${encodeURIComponent(p.id)}` : null),
  liveEvent: (p) => (p.slug ? `/public/live-events/${encodeURIComponent(p.slug)}` : null),
  // B7: a competition link (/schedule/:id) resolves the competition's sport.
  // /schedule without an id is not an entity page (no lookup).
  schedule: (p) => (p.id ? `/public/tournaments/${encodeURIComponent(p.id)}` : null),
}

/** The entity's sport_id in a detail payload ({ sport_id } or { game: { sport_id } }, …). */
export function findSportId(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null
  const top = payload as Record<string, unknown>
  if (typeof top.sport_id === 'string') return top.sport_id
  for (const v of Object.values(top)) {
    if (v && typeof v === 'object' && typeof (v as Record<string, unknown>).sport_id === 'string') {
      return (v as Record<string, string>).sport_id
    }
  }
  return null
}

type Entry = { status: 'pending'; promise: Promise<void> } | { status: 'done'; sport: string | null }
const lookups = new Map<string, Entry>()

/**
 * The pathname the session's first templated page rendered at: only that URL
 * is a "deep link". Recorded on the first gate render (router pathname).
 */
let initialPath: string | null = null
export function isInitialPath(pathname: string): boolean {
  if (initialPath === null) initialPath = pathname
  return pathname === initialPath
}

/**
 * Forget the per-endpoint lookups only (the deep-link slot `initialPath`
 * stays): a private site's admission (registration step 33) would otherwise
 * keep a lookup that a 401 recorded as null for the rest of the session.
 */
export function resetEntitySportLookups(): void {
  lookups.clear()
}

/** Test hook. */
export function resetEntitySportGate(): void {
  lookups.clear()
  initialPath = null
}

export function readEntitySport(endpoint: string): string | null {
  const hit = lookups.get(endpoint)
  if (hit?.status === 'done') return hit.sport
  if (hit) throw hit.promise
  const promise = api.request<unknown>(endpoint, { noSportStamp: true }).then(
    (payload) => { lookups.set(endpoint, { status: 'done', sport: findSportId(payload) }) },
    () => { lookups.set(endpoint, { status: 'done', sport: null }) }, // the page shows its own not-found
  )
  lookups.set(endpoint, { status: 'pending', promise })
  throw promise
}

